import { Fragment, useEffect, useState } from "react";
import { api, type AdminStatus, type JobRun, type Report } from "../lib/api.js";
import { AuthGate } from "../components/AuthGate.js";
import { ErrorText, Field } from "../components/ui.js";

export function AdminPage() {
  return (
    <AuthGate>
      <AdminInner />
    </AuthGate>
  );
}

const uploadKinds: Array<{ kind: string; label: string }> = [
  { kind: "jsf-shina", label: "日証金 品貸料率一覧 CSV" },
  { kind: "jsf-seigen", label: "日証金 制限措置等一覧 CSV" },
  { kind: "jsf-meigara", label: "日証金 貸借取引対象銘柄一覧 CSV" },
  { kind: "jpx-daily", label: "JPX 日々公表銘柄 HTML" },
  { kind: "jpx-margin", label: "JPX 増担保規制 HTML" },
  { kind: "stocks", label: "JPX 東証上場銘柄一覧 Excel" },
];

const masterTemplate = JSON.stringify(
  {
    stockCode: "0000",
    recordMonths: [3, 9],
    recordDayType: "month_end",
    categoryTags: ["quo"],
    tiers: [{ minShares: 100, description: "QUOカード 1,000円分", valueYen: 1000 }],
    longTermRule: { type: "shares_at_each_record_date", requiredCount: 3, checkMonths: [3, 9], minSharesByTier: { "100": 100 } },
    expiryRule: { type: "months_after_record", months: 12 },
    arrivalOffsetMonths: 3,
    sourceUrl: "https://example.com/ir/yutai",
    status: "published",
  },
  null,
  2,
);

function AdminInner() {
  const [runs, setRuns] = useState<JobRun[]>([]);
  const [jobs, setJobs] = useState<Array<{ name: string; crons: string[] }>>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [masters, setMasters] = useState<Array<{ stockCode: string; status: string; updatedAt: string }>>([]);
  const [log, setLog] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [masterJson, setMasterJson] = useState(masterTemplate);
  const [draftCode, setDraftCode] = useState("");
  const [draftUrl, setDraftUrl] = useState("");
  const [draftText, setDraftText] = useState("");
  const [draftImage, setDraftImage] = useState<{ data: string; type: string } | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [yahooCode, setYahooCode] = useState("8591");
  const [openRun, setOpenRun] = useState<string | null>(null);
  const [status, setStatus] = useState<AdminStatus | null>(null);

  const reload = async () => {
    try {
      setStatus(await api.admin.status());
      const j = await api.admin.jobs();
      setRuns(j.runs);
      setJobs(j.jobs);
      setReports((await api.admin.reports()).reports);
      setMasters((await api.admin.masters()).masters);
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => {
    void reload();
  }, []);

  const run = async (name: string) => {
    setError(null);
    try {
      const r = await api.admin.runJob(name);
      setLog(r.log);
      await reload();
    } catch (e) {
      setError(e);
    }
  };

  const upload = async (kind: string, file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const r = await api.admin.upload(kind, file);
      setLog(r.log);
      await reload();
    } catch (e) {
      setError(e);
    }
  };

  const saveMaster = async () => {
    setError(null);
    try {
      const body = JSON.parse(masterJson) as { stockCode: string };
      await api.admin.putMaster(body.stockCode, body);
      setLog([`優待マスタ ${body.stockCode} を保存しました`]);
      await reload();
    } catch (e) {
      setError(e);
    }
  };

  const draft = async () => {
    setError(null);
    setDrafting(true);
    try {
      const r = await api.admin.draftMaster({
        stockCode: draftCode.toUpperCase(),
        ...(draftUrl ? { url: draftUrl } : {}),
        ...(draftText ? { text: draftText } : {}),
        ...(draftImage ? { imageBase64: draftImage.data, imageMediaType: draftImage.type } : {}),
      });
      setMasterJson(JSON.stringify(r.draft, null, 2));
      setLog([`AI 下書きを作成しました（確信度: ${r.confidence}）。内容を確認して「保存して公開」を押してください。`]);
    } catch (e) {
      setError(e);
    } finally {
      setDrafting(false);
    }
  };

  const onImage = (file: File | undefined) => {
    if (!file) return setDraftImage(null);
    const reader = new FileReader();
    reader.onload = () => setDraftImage({ data: String(reader.result), type: file.type || "image/jpeg" });
    reader.readAsDataURL(file);
  };

  return (
    <section>
      <h1>管理</h1>
      <ErrorText error={error} />
      {log.length > 0 && (
        <pre className="card log">{log.join("\n")}</pre>
      )}

      {status && (
        <div className="card">
          <h2>状態</h2>
          <table className="table">
            <tbody>
              {status.jobs.map((j) => (
                <tr key={j.name}>
                  <td>{j.name}</td>
                  <td className={j.last?.status === "failed" ? "error" : j.last ? "" : "muted"}>{j.last ? `${j.last.status} ${j.last.startedAt.slice(5, 16).replace("T", " ")}` : "未実行"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted">
            銘柄 {status.counts.stocks} / 株価 {status.counts.prices} / 制限 {status.counts.restrictionsActive} / 逆日歩 {status.counts.gyakuhibuRows} / 優待マスタ {status.counts.yutaiMasterPublished} / ユーザー {status.counts.users} / 未処理報告 {status.counts.openReports}
          </p>
          <p className="muted">
            設定: {Object.entries(status.config).map(([k, v]) => `${k}=${v ? "○" : "×"}`).join(" ")}
          </p>
        </div>
      )}
      <div className="card">
        <h2>ジョブ</h2>
        <div className="row wrap">
          {jobs.map((j) => (
            <button key={j.name} className="btn small" type="button" onClick={() => run(j.name)}>
              {j.name} を実行
            </button>
          ))}
        </div>
        <p className="muted">行をタップすると詳細ログが開きます。</p>
        <table className="table">
          <thead>
            <tr>
              <th>ジョブ</th>
              <th>開始</th>
              <th>状態</th>
            </tr>
          </thead>
          <tbody>
            {runs.slice(0, 15).map((r) => (
              <Fragment key={r.id}>
                <tr onClick={() => setOpenRun(openRun === r.id ? null : r.id)} style={{ cursor: "pointer" }}>
                  <td>{r.job}</td>
                  <td>{r.startedAt.slice(0, 16).replace("T", " ")}</td>
                  <td className={r.status === "failed" ? "error" : ""}>{r.status}</td>
                </tr>
                {openRun === r.id && (
                  <tr>
                    <td colSpan={3}>
                      <pre className="log">{r.detail || "（詳細なし）"}</pre>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card form">
        <h2>手動アップロード</h2>
        {uploadKinds.map((u) => (
          <Field key={u.kind} label={u.label}>
            <input className="input" type="file" onChange={(e) => upload(u.kind, e.target.files?.[0])} />
          </Field>
        ))}
      </div>

      <div className="card form">
        <h2>AI 補助登録</h2>
        <p className="muted">IR ページの URL、本文の貼り付け、優待券の写真のいずれかから下書きを作ります。下書きは下の JSON 欄に入るので、確認してから公開してください。</p>
        <Field label="証券コード">
          <input className="input" value={draftCode} onChange={(e) => setDraftCode(e.target.value)} pattern="[0-9][0-9A-Za-z]{3}" />
        </Field>
        <Field label="IR ページ URL">
          <input className="input" type="url" value={draftUrl} onChange={(e) => setDraftUrl(e.target.value)} />
        </Field>
        <Field label="本文（任意）">
          <textarea className="input" rows={4} value={draftText} onChange={(e) => setDraftText(e.target.value)} />
        </Field>
        <Field label="優待券の写真（任意）">
          <input className="input" type="file" accept="image/*" onChange={(e) => onImage(e.target.files?.[0])} />
        </Field>
        <button className="btn primary" type="button" disabled={drafting || !draftCode} onClick={draft}>
          {drafting ? "作成中…" : "下書きを作る"}
        </button>
      </div>

      <div className="card form">
        <h2>Yahoo!ファイナンス 解析テスト</h2>
        <div className="row">
          <input className="input" value={yahooCode} onChange={(e) => setYahooCode(e.target.value)} />
          <button
            className="btn"
            type="button"
            onClick={async () => {
              setError(null);
              try {
                const r = await api.admin.yahooTest(yahooCode.toUpperCase());
                setLog([JSON.stringify(r, null, 2)]);
              } catch (e) {
                setError(e);
              }
            }}
          >
            1 銘柄を取得して解析
          </button>
        </div>
      </div>

      <div className="card form">
        <h2>優待マスタ（JSON）</h2>
        <p className="muted">
          登録済み: {masters.length}件。コードをクリックで読み込み。
        </p>
        <div className="row wrap">
          {masters.map((m) => (
            <button
              key={m.stockCode}
              className={`btn small ${m.status === "published" ? "" : "danger"}`}
              type="button"
              onClick={async () => {
                const r = await api.admin.master(m.stockCode);
                setMasterJson(JSON.stringify(r.master, null, 2));
              }}
            >
              {m.stockCode}
            </button>
          ))}
        </div>
        <textarea className="input mono" rows={18} value={masterJson} onChange={(e) => setMasterJson(e.target.value)} />
        <div className="row">
          <button className="btn primary" type="button" onClick={saveMaster}>
            保存して公開
          </button>
          <button className="btn" type="button" onClick={() => setMasterJson(masterTemplate)}>
            テンプレート
          </button>
        </div>
      </div>

      <div className="card">
        <h2>ユーザー報告（未処理 {reports.length}件）</h2>
        <ul className="list">
          {reports.map((r) => (
            <li key={r.id}>
              <div>
                <strong>{r.stockCode}</strong> [{r.kind}] {r.message}
                {r.sourceUrl && (
                  <>
                    {" "}
                    <a href={r.sourceUrl} target="_blank" rel="noopener noreferrer">
                      情報源
                    </a>
                  </>
                )}
              </div>
              <div className="row">
                <button className="btn small" type="button" onClick={() => api.admin.setReport(r.id, "accepted").then(reload)}>
                  採用
                </button>
                <button className="btn small danger" type="button" onClick={() => api.admin.setReport(r.id, "rejected").then(reload)}>
                  却下
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
